/**
 * 將 Markdown 語法轉換成安全的 HTML 字串
 * @param markdown - Markdown 原始文字
 * @param renderType: 轉換類型，simple: 只有 List and link, default: 全部
 * @returns 經過 DOMPurify 消毒後的 HTML 字串
 */

import DOMPurify from 'isomorphic-dompurify';
import MarkdownIt from 'markdown-it';

const MarkdownItProcessLink = (md: MarkdownIt): void => {
  // 將 Markdown 語法的連結加上 `target="_blank"`、`rel="noopener noreferrer"`

  const defaultLinkRender =
    md.renderer.rules.link_open ||
    function (tokens, idx, options, env, self) {
      return self.renderToken(tokens, idx, options);
    };

  md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
    tokens[idx].attrSet('target', '_blank');
    tokens[idx].attrSet('rel', 'noopener noreferrer');
    return defaultLinkRender(tokens, idx, options, env, self);
  };
};

const provideMarkdownHtml = (
  markdown: string | null,
  renderType: 'simple' | 'default'
): string => {
  if (!markdown) return '';

  /**
   * 根據 renderType 參數決定使用哪種 MarkdownIt 設定
   * simple: 只有 List and link, default: 全部
   */

  const md = ((): MarkdownIt => {
    if (renderType === 'simple') {
      return MarkdownIt('zero').enable(['paragraph', 'text', 'link', 'list']);
    }
    return MarkdownIt({ html: true });
  })();

  MarkdownItProcessLink(md);

  // 將轉換後的內容做 sanitize 處理，避免 XSS 攻擊
  // NOTE: 使用 MarkdownIt 轉換的 HTML 必須使用 DOMPurify 消毒，用 sanitizeHtml 會壞掉

  const result = md.render(markdown);

  return DOMPurify.sanitize(result, {
    FORBID_TAGS: ['script', 'style'],
    FORBID_ATTR: ['style', 'onerror', 'onclick', 'onload'],
    ADD_ATTR: ['target', 'rel'],
  });
};

export default provideMarkdownHtml;
